import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, CalendarOff } from "lucide-react";
import { fmtDate } from "@/lib/utils-app";
import { toast } from "sonner";

function istToday() {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const p = Object.fromEntries(fmt.formatToParts(new Date()).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
}

export default function HolidaysPage() {
  const [parks, setParks] = useState([]);
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ date: istToday(), park_id: "all", reason: "" });

  const refresh = async () => {
    const r = await api.get("/admin/holidays");
    setList(r.data || []);
  };

  useEffect(() => {
    api.get("/parks").then((r) => setParks(r.data));
    refresh();
  }, []);

  const submit = async () => {
    if (!form.date) return toast.error("Date required");
    setBusy(true);
    try {
      await api.post("/admin/holidays", {
        date: form.date,
        park_id: form.park_id === "all" ? null : form.park_id,
        reason: form.reason || null,
      });
      toast.success("Holiday added");
      setForm({ date: istToday(), park_id: "all", reason: "" });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (h) => {
    if (!window.confirm(`Remove holiday on ${fmtDate(h.date)}?`)) return;
    try {
      await api.delete(`/admin/holidays/${h.id}`);
      refresh();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <AppShell title="Holidays" subtitle="Mark a date as holiday for a single park or all parks">
      <div className="flex flex-col gap-4">
        <Card>
          <CardContent className="grid gap-2 p-3 md:grid-cols-4">
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              data-testid="holiday-date"
            />
            <Select value={form.park_id} onValueChange={(v) => setForm((f) => ({ ...f, park_id: v }))}>
              <SelectTrigger data-testid="holiday-park"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All parks</SelectItem>
                {parks.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Reason (optional)"
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              data-testid="holiday-reason"
            />
            <Button onClick={submit} disabled={busy} data-testid="holiday-add-button">
              <Plus className="mr-1.5 h-4 w-4" /> Add holiday
            </Button>
          </CardContent>
        </Card>

        {list.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center gap-1 p-8 text-center text-sm text-muted-foreground">
              <CalendarOff className="h-6 w-6 text-muted-foreground/60" />
              No holidays defined.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-2" data-testid="holiday-list">
            {list.map((h) => (
              <Card key={h.id} data-testid={`holiday-${h.id}`}>
                <CardContent className="flex items-center gap-3 p-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-violet-100 text-violet-700">
                    <CalendarOff className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{fmtDate(h.date)}</div>
                    <div className="text-xs text-muted-foreground">
                      {h.park_id ? <>Park: <b>{h.park_name || h.park_id}</b></> : <>All parks</>}
                      {h.reason ? <> · {h.reason}</> : null}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => remove(h)} aria-label="Remove">
                    <Trash2 className="h-4 w-4 text-rose-600" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
